import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { SELECTOR_TYPE, updateSelectorType } from "../../../reducers/period";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchCbrActionReportData } from "./CbrActionReportDataLayer";
import { toggleSortDirection, setAffAbosToExpand } from "../../../reducers/cbrArReport";
import { useActionReportTreeView } from "../common/hooks/useActionReportTreeView";
import defaultContent from "../../../content/actionReportsCbr";
import { MODULE_CBR_V2 } from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import { IconInfoCircle } from "../../../common/icons/IconInfoCircle/IconInfoCircle";
import {
  cbrPageViewTrack,
  cbrExportTrack,
  cbrSortAscendingTrack,
  cbrSortDescendingTrack,
  cbrProfileTrack,
  cbrAddToWatchlistTrack,
  cbrRemoveFromWatchlistTrack,
  cbrInfoTrack,
  cbrPhoneIconTrack,
  cbrMessageIconTrack,
} from "../../../common/tealium/ActionReports/CbrTrack";
import "../ActionReports.scss";
import "./CbrActionReport.scss";
import { getCurrentPeriod } from "../../../common/util/period";
import { storedLocale } from "../../Authentication/util";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";

const DEFAULT_KPIS = [
  KPI_ID.CBR_PERIOD_REMAINING,
  KPI_ID.CBR_VCS_PERCENT,
  KPI_ID.CBR_START_MONTH,
  KPI_ID.CBR_END_MONTH,
];

const createFieldConfig = (kpiIds: string[], tableColumns: { [key: string]: string }) => [
  ...kpiIds.map((kpiId) => ({
    id: kpiId,
    kpi: kpiId,
    title: tableColumns[kpiId] || kpiId,
  })),
  { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
];

export default function CbrActionReport() {
  const { t } = useTranslation(["actionReportsCbr", "leafAboName"]);
  const dispatch = useAppDispatch();
  const { sortDirection } = useAppSelector((state) => state.cbrActionReportTracker);
  const currentPeriod = getCurrentPeriod();

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
  }, [dispatch]);

  const { reportData, isLoading, errorStatus } = FetchCbrActionReportData(currentPeriod);

  const hasData = !isLoading && !errorStatus && reportData.length > 0;

  const allAbos = useMemo(
    () => reportData.map((record) => ({ aff: String(record.aff), abo: String(record.abo) })),
    [reportData],
  );

  const { isTreeView, handleToggle } = useActionReportTreeView({
    isLoading,
    hasData,
    setAffAbosToExpand,
    allAbos,
  });

  const [searchText, setSearchText] = useState<string>("");
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    cbrPageViewTrack(currentPeriod);
  }, [currentPeriod]);

  const handleSortClick = () => {
    if (sortDirection === "asc") {
      cbrSortDescendingTrack();
    } else {
      cbrSortAscendingTrack();
    }
    dispatch(toggleSortDirection());
  };

  const handleWatchlistTrack = (isAdding: boolean) => {
    if (isAdding) {
      cbrAddToWatchlistTrack();
    } else {
      cbrRemoveFromWatchlistTrack();
    }
  };

  const tableColumns = t("table.columns", {
    ns: "actionReportsCbr",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as { [key: string]: string };

  const fieldConfig = useMemo(() => createFieldConfig(DEFAULT_KPIS, tableColumns), [tableColumns]);

  const sortedReportData = useMemo(() => {
    if (sortDirection === "desc") return [...reportData].reverse();
    return reportData;
  }, [reportData, sortDirection]);

  const searchPool = useMemo(
    () =>
      sortedReportData.map((record, index) => ({
        abo: record.abo,
        aff: record.aff,
        aboName: record.aboName,
        isoCountry: record.isoCountry,
        index,
      })),
    [sortedReportData],
  );

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `#${entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        setScrollToIndex(found.index);
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToIndex(undefined);
  }, []);

  const totalAboCount = sortedReportData.length;

  const exportAffAbos = useMemo(
    () => sortedReportData.map((record) => ({ affAbo: `${record.aff}-${record.abo}` })),
    [sortedReportData],
  );

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_CBR_V2]: t("export.exportTypeLabel", defaultContent.export.exportTypeLabel).toString(),
    }),
    [t],
  );

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  const renderContent = () => {
    if (isTreeView) {
      return <ActionReportsTreeView path="cbr" />;
    }

    if (isLoading) {
      return (
        <div className="CbrAR__listViewWrapper">
          <ActionReportListViewLoader />
        </div>
      );
    }

    if (errorStatus) {
      return (
        <div className="CbrAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (totalAboCount === 0) {
      return (
        <div className="CbrAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    return (
      <div className="CbrAR__listViewWrapper">
        <ActionReportListView
          selectedKpis={DEFAULT_KPIS}
          reportId="cbrAR"
          reportData={sortedReportData}
          period={currentPeriod}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          scrollToIndex={scrollToIndex}
          onProfileTrack={cbrProfileTrack}
          onWatchlistTrack={handleWatchlistTrack}
          onPhoneTrack={cbrPhoneIconTrack}
          onMessageTrack={cbrMessageIconTrack}
          showImageBlock={true}
        />
      </div>
    );
  };

  const pageTitle = getCanonicalTitle({ id: "cbrClockCheck" }, t, {});

  return (
    <main className="CbrAR">
      <ActionReportHeader
        title={pageTitle}
        description={
          <span className="CbrAR__description">
            {isLoading ? (
              <LeafRectangleLoader isLoading={true} width="2rem" height="0.75rem" margin="0" />
            ) : (
              <strong>{totalAboCount}</strong>
            )}{" "}
            {t("title.pageDescription", defaultContent.title.pageDescription).toString()}
          </span>
        }
        reportLength={totalAboCount}
        infoButton={
          <button
            type="button"
            onClick={() => {
              cbrInfoTrack();
              setShowInfoModal(true);
            }}
            className="CbrAR__infoButton"
            aria-label={t("info.cbrClockInfoLabel", defaultContent.info.cbrClockInfoLabel as string).toString()}
          >
            <IconInfoCircle />
          </button>
        }
      />
      <ActionReportToolbar
        reportLength={totalAboCount}
        isAllEnriched={true}
        treeViewToggle={{
          enabled: true,
          isTreeView: isTreeView,
          onToggle: handleToggle,
          disabled: !hasData,
          label: t("losTreeView", defaultContent.losTreeView) as string,
        }}
        sort={{
          enabled: false,
          direction: sortDirection,
          onClick: handleSortClick,
          disabled: isLoading || totalAboCount === 0,
        }}
        search={
          !isTreeView
            ? {
                enabled: true,
                searchCallback: handleSearchCallback,
                selectionCallback: handleSelectionCallback,
                searchText: searchText,
                clearFunction: handleClearSearch,
                disabled: isLoading || searchPool.length === 0,
              }
            : undefined
        }
        export={{
          moduleId: MODULE_CBR_V2,
          exportAffAbos: exportAffAbos,
          selectedKpis: DEFAULT_KPIS,
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_CBR_V2,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={cbrExportTrack}
      />
      <div className="CbrAR__content">{renderContent()}</div>
      <div>
        <BonusInfoFooter />
      </div>
      <LeafModal
        open={showInfoModal}
        onCloseCallbackFn={() => setShowInfoModal(false)}
        title={t("title.pageTitle", defaultContent.title.pageTitle).toString()}
        id="cbrClockInfoModal"
      >
        <div className="CbrAR__infoModal">
          <p className="CbrAR__infoModal__description">
            {t("info.cbrClockDescription", defaultContent.info.cbrClockDescription as string).toString()}
          </p>
        </div>
      </LeafModal>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_CBR_V2}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_CBR_V2, true)}
      />
    </main>
  );
}
